<template>
    <div class="flex mt-[42px] mb-[42px] justify-between h-[42px]">
        <h1
            class="text-[21px] font-medium leading-normal tracking-small text-gray-800"
        >
            {{ $t("vue.support") }}
        </h1>
    </div>

    <div class="mx-auto pb-[42px] xl:max-w-[1528px]">
        <div class="space-y-8">
            <div class="grid-cols-layout pb-5 lg:grid">
                <div class="mb-6 lg:mb-0 lg:mt-4">
                    <h3
                        class="gray-800 mb-[5px] text-lg font-medium leading-normal tracking-small"
                    >
                        {{ $t("vue.support_ticket_title") }}
                    </h3>
                    <p class="max-w-xs text-sm text-gray-500">
                        {{ $t("vue.support_ticket_description") }}
                    </p>
                </div>

                <div
                    class="mx-auto w-full rounded-md bg-white shadow-card lg:mx-0"
                >
                    <form
                        v-if="!showSubmitted"
                        class="flex flex-col"
                        @submit.prevent="submitForm"
                    >
                        <div class="px-[21px] py-[21px]">
                            <div class="space-y-[18px]">
                                <div>
                                    <label
                                        class="text-[12px] font-medium leading-[140%] text-[#111827]"
                                    >
                                        {{
                                            $t("vue.support_ticket_name_label")
                                        }}
                                        <span class="text-[#FF421E]"
                                            >*</span
                                        > </label
                                    ><br />
                                    <input
                                        v-model.trim="form.name"
                                        type="text"
                                        class="mt-[6px] h-[32px] w-full max-w-[240px] rounded-[6px] border border-[#E5E7EB] bg-white px-[10px] text-[12px] text-gray-600 outline-none"
                                        :placeholder="
                                            $t(
                                                'vue.support_ticket_name_placeholder',
                                            )
                                        "
                                        :disabled="submitting"
                                    />
                                </div>

                                <div>
                                    <label
                                        class="text-[12px] font-medium leading-[140%] text-[#111827]"
                                    >
                                        {{
                                            $t(
                                                "vue.support_ticket_category_label",
                                            )
                                        }}
                                        <span class="text-[#FF421E]"
                                            >*</span
                                        > </label
                                    ><br />
                                    <select
                                        v-model="form.category"
                                        class="mt-[6px] h-[32px] w-full max-w-[240px] rounded-[6px] border border-[#E5E7EB] bg-white px-[10px] text-[12px] text-gray-600 outline-none"
                                        :disabled="submitting"
                                    >
                                        <option value="" disabled>
                                            {{
                                                $t(
                                                    "vue.support_ticket_category_placeholder",
                                                )
                                            }}
                                        </option>
                                        <option
                                            v-for="option in categoryOptions"
                                            :key="option.value"
                                            :value="option.value"
                                        >
                                            {{ option.label }}
                                        </option>
                                    </select>
                                </div>

                                <div>
                                    <label
                                        class="text-[12px] font-medium leading-[140%] text-[#111827]"
                                    >
                                        {{
                                            $t(
                                                "vue.support_ticket_description_label",
                                            )
                                        }}
                                        <span class="text-[#FF421E]"
                                            >*</span
                                        > </label
                                    ><br />
                                    <textarea
                                        v-model.trim="form.description"
                                        class="mt-[6px] min-h-[130px] w-full rounded-[6px] border border-[#E5E7EB] bg-white px-[10px] py-[8px] text-[12px] text-gray-600 outline-none"
                                        :disabled="submitting"
                                    ></textarea>
                                </div>
                                <div class="flex items-center gap-3">
                                    <button
                                        type="button"
                                        class="h-[28px] rounded-[6px] border border-[#E5E7EB] bg-white px-[10px] text-[10.5px] font-medium text-gray-600"
                                        :disabled="submitting"
                                        @click="triggerFileSelect"
                                    >
                                        {{
                                            $t("vue.support_ticket_choose_file")
                                        }}
                                    </button>
                                    <div
                                        class="flex items-center gap-2 text-[10.5px] text-gray-400"
                                    >
                                        <button
                                            v-if="form.fileName"
                                            type="button"
                                            class="text-gray-400 hover:text-gray-500"
                                            @click="clearFile"
                                        >
                                            <XMarkIcon class="h-4 w-4" />
                                        </button>
                                        <span>
                                            {{
                                                form.fileName
                                                    ? form.fileName
                                                    : $t(
                                                          "vue.support_ticket_no_file",
                                                      )
                                            }}
                                        </span>
                                    </div>
                                    <input
                                        ref="fileInput"
                                        type="file"
                                        class="hidden"
                                        :disabled="submitting"
                                        accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                                        @change="handleFileChange"
                                    />
                                </div>
                            </div>
                        </div>

                        <div
                            v-if="errorMessage"
                            class="px-[21px] pb-[12px] text-[12px] text-[#FF421E]"
                        >
                            {{ errorMessage }}
                        </div>

                        <div
                            class="flex justify-end border-t border-[#EDEFF1] px-[21px] py-[21px]"
                        >
                            <button
                                type="submit"
                                class="h-[32px] rounded-[6px] px-[14px] text-[10.5px] font-medium leading-[142%] text-white transition"
                                :class="
                                    canSubmit
                                        ? 'bg-[#088BEA] hover:bg-[#0679CC]'
                                        : 'cursor-not-allowed bg-[#E5E7EB] opacity-70'
                                "
                                :disabled="!canSubmit || submitting"
                            >
                                {{
                                    submitting
                                        ? $t("vue.saving_in_process")
                                        : $t("vue.support_ticket_submit")
                                }}
                            </button>
                        </div>
                    </form>

                    <div v-else class="px-[42px] py-[42px]">
                        <div
                            class="flex flex-col items-center justify-center text-center"
                        >
                            <img
                                :src="supportSuccessUrl"
                                alt=""
                                class="h-[140px] w-auto"
                            />
                            <h4
                                class="mt-[18px] text-[14px] font-semibold text-gray-800"
                            >
                                {{ $t("vue.support_ticket_submitted_title") }}
                            </h4>
                            <p class="mt-[6px] text-[10.5px] text-gray-500">
                                {{
                                    $t(
                                        "vue.support_ticket_submitted_description",
                                    )
                                }}
                            </p>
                            <button
                                type="button"
                                class="mt-[18px] h-[28px] rounded-[6px] bg-[#088BEA] px-[14px] text-[10.5px] font-medium text-white hover:bg-[#0679CC]"
                                @click="resetForm"
                            >
                                {{ $t("vue.support_ticket_submit_another") }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue";
import { XMarkIcon } from "@heroicons/vue/24/outline";
import axios from "axios";
import route from "ziggy-js";
import { useTranslations } from "@/composables/useTranslations.ts";

const supportSuccessUrl = new URL(
    "/resources/js/assets/images/mail-sent-trajectory.svg",
    import.meta.url,
).href;

const { t } = useTranslations();

const showSubmitted = ref(false);
const fileInput = ref<HTMLInputElement | null>(null);
const submitting = ref(false);
const errorMessage = ref<string | null>(null);

const MAX_ATTACHMENT_BYTES = 4 * 1024 * 1024;
const ALLOWED_ATTACHMENT_MIME_TYPES = ["image/jpeg", "image/png"];
const ALLOWED_ATTACHMENT_EXTENSIONS = ["jpg", "jpeg", "png"];

const form = ref({
    name: "",
    category: "",
    description: "",
    file: null as File | null,
    fileName: "",
});

const canSubmit = computed(() => {
    return (
        form.value.name.trim().length > 0 &&
        form.value.category.trim().length > 0 &&
        form.value.description.trim().length > 0
    );
});

type CategoryOption = {
    value: string;
    label: string;
};

const categoryOptions = computed<CategoryOption[]>(() => [
    {
        value: "GENERAL_INQUIRY",
        label: t("vue.support_ticket_category_general_inquiry"),
    },
    {
        value: "PRODUCT_ISSUE",
        label: t("vue.support_ticket_category_product_issue"),
    },
    {
        value: "BILLING_ISSUE",
        label: t("vue.support_ticket_category_billing_issue"),
    },
    {
        value: "FEATURE_REQUEST",
        label: t("vue.support_ticket_category_feature_request"),
    },
    {
        value: "ACCOUNT_ASSISTANCE",
        label: t("vue.support_ticket_category_account_assistance"),
    },
    {
        value: "TECHNICAL_ASSISTANCE",
        label: t("vue.support_ticket_category_technical_assistance"),
    },
    {
        value: "BUG_REPORTS",
        label: t("vue.support_ticket_category_bug_reports"),
    },
]);

const triggerFileSelect = () => {
    fileInput.value?.click();
};

const getFileExtension = (filename: string): string => {
    const lastDotIndex = filename.lastIndexOf(".");
    if (lastDotIndex === -1 || lastDotIndex === filename.length - 1) {
        return "";
    }
    return filename.slice(lastDotIndex + 1).toLowerCase();
};

const validateAttachment = (file: File): string | null => {
    if (file.size > MAX_ATTACHMENT_BYTES) {
        return t("vue.support_ticket_file_too_large");
    }

    const extension = getFileExtension(file.name);
    const hasValidExtension = ALLOWED_ATTACHMENT_EXTENSIONS.includes(extension);
    const hasValidMimeType =
        file.type !== "" && ALLOWED_ATTACHMENT_MIME_TYPES.includes(file.type);

    if (!hasValidExtension && !hasValidMimeType) {
        return t("vue.support_ticket_file_invalid_type");
    }

    return null;
};

const handleFileChange = (event: Event) => {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;

    errorMessage.value = null;

    if (!file) {
        form.value.file = null;
        form.value.fileName = "";
        return;
    }

    const validationMessage = validateAttachment(file);

    if (validationMessage) {
        errorMessage.value = validationMessage;
        clearFile();
        return;
    }

    form.value.file = file;
    form.value.fileName = file.name;
};

const clearFile = () => {
    form.value.file = null;
    form.value.fileName = "";
    if (fileInput.value) {
        fileInput.value.value = "";
    }
};

const resetForm = () => {
    showSubmitted.value = false;
    errorMessage.value = null;
    form.value = {
        name: "",
        category: "",
        description: "",
        file: null,
        fileName: "",
    };
    clearFile();
};

const submitForm = async () => {
    if (!canSubmit.value || submitting.value) {
        return;
    }

    submitting.value = true;
    errorMessage.value = null;

    try {
        if (form.value.file) {
            const validationMessage = validateAttachment(form.value.file);
            if (validationMessage) {
                errorMessage.value = validationMessage;
                clearFile();
                return;
            }
        }

        const formData = new FormData();

        formData.append("name", form.value.name.trim());
        formData.append("category", form.value.category);
        formData.append("description", form.value.description.trim());

        if (form.value.file) {
            formData.append("file", form.value.file);
        }

        await axios.post(route("support.store"), formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });

        showSubmitted.value = true;
    } catch (error) {
        const responseMessage =
            axios.isAxiosError(error) && error.response?.data?.message
                ? String(error.response.data.message)
                : "";

        errorMessage.value =
            responseMessage !== "" &&
            responseMessage !== "Support ticket submission failed."
                ? responseMessage
                : t("vue.support_ticket_submit_failed");
    } finally {
        submitting.value = false;
    }
};
</script>
